<script lang="ts">
/*VUE2*/  export default { inheritAttrs: false };
</script>

<script setup lang="ts">
import { VIcon } from 'vuetify/lib';
import { computed, useAttrs } from 'vue';
import { URL_ROOT } from '@mimir/web/configConstants';
import { customIconNameSet, customPngIconNameSet } from './MIcon.constants';
import type { IconName } from './MIcon.types';
import { makeSizeProps, uncheckedProp } from '../props';
import { toV2Size } from '../vuetify2Conversions';
import { useVue2Listeners } from '../vue2Bridge';

// TODO: MODE-3 gives clickable material icons role="button", but Vuetify's md set drops click listeners, so they have no working click until #22289 lands with an icon-set component that keeps them.


const isVue2 = !!import.meta.env.VUE_APP_V2;

const props = defineProps({
  icon: { ...uncheckedProp<IconName | null>(), required: true },
  ...makeSizeProps(),
  color: { type: String },
  disabled: { type: Boolean },
});

const size = computed(() => props.size ?? 'default');

const attrs = useAttrs();

// initializeVue3 Vuetify has the sets md and mdi, the use for non-default sets needs {set}: which is handled here for Vue 2 and Vue 3.
// Can be modified to Vue 3 only during migration cleanup.
const resolvedIcon = computed(() => {
  if (!isVue2 && props.icon && props.icon.startsWith('mdi-')) {
    return 'mdi:' + props.icon;
  }
  return props.icon;
});

const isCustomIcon = computed(() => customIconNameSet.has(resolvedIcon.value || ''));
const isPngIcon = computed(() => customPngIconNameSet.has(resolvedIcon.value || ''));
const isCustomSize = computed(() => typeof size.value === 'number');
// Only named non-default sizes map to a CSS modifier class; 'default' and
// custom numeric sizes are handled by the base rule and inline styles.
const sizeClass = computed(() =>
  typeof size.value === 'string' && size.value !== 'default' ? size.value : null,
);

// Raster icons render as <img> and cannot be recoloured, so only size and
// disabled state apply. They use the custom-png-icon class (sized to match
// custom-icon) so they are not hit by consumers' mask-recolouring rules.
const pngSrc = computed(() => `${URL_ROOT}assets/icons/${resolvedIcon.value}.png`);
const pngStyle = computed(() => {
  const style: Record<string, string | number> = { opacity: props.disabled ? 0.3 : 1 };
  if (isCustomSize.value) {
    const px = `${size.value}px`;
    style.width = px;
    style.height = px;
  }
  return style;
});

const maskStyle = computed(() => {
  if (resolvedIcon.value === null) {
    return {};
  }
  const url = `${URL_ROOT}assets/icons/${resolvedIcon.value}.svg`;
  const style: Record<string, string | number> = {
    '-webkit-mask': `url(${url}) no-repeat center`,
    'mask': `url(${url}) no-repeat center`,
    'background-color': props.color ?? 'var(--v-strong-base)',
    'opacity': props.disabled ? 0.3 : 1,
  };
  if (isCustomSize.value) {
    const px = `${size.value}px`;
    style.width = px;
    style.height = px;
  }
  return style;
});

const viconPropsV2 = computed(() => ({
  color: props.color,
  disabled: props.disabled,
  ...toV2Size(size.value),
  ...attrs,
}));

/*
  Vuetify 2 renders named sizes as these inline pixel sizes and leaves 'default'
  to CSS. Vuetify 3 scales named sizes in em with the surrounding font, and the
  rem override in vuetify3.styl loses to VIcon.css whenever that loads later, so
  the Vue 3 root passes the pixel values to keep MIcon the same size in both builds.
*/
const vuetify2NamedSizePx = { 'x-small': 12, small: 16, large: 36, 'x-large': 40 } as const;

const viconProps = computed(() => ({
  icon: resolvedIcon.value,
  size: typeof size.value === 'string' && size.value !== 'default' ? vuetify2NamedSizePx[size.value] : size.value,
  color: props.color,
  disabled: props.disabled,
  ...attrs,
}));

const vue2Listeners = useVue2Listeners([]);
</script>

<template>
  <div
    v-if="isCustomIcon"
    aria-hidden="true"
    v-bind="attrs"
    :class="['custom-icon', sizeClass]"
    :style="maskStyle"
    v-on="vue2Listeners"
  />
  <img
    v-else-if="isPngIcon"
    aria-hidden="true"
    v-bind="attrs"
    :src="pngSrc"
    :class="['custom-png-icon', sizeClass]"
    :style="pngStyle"
    v-on="vue2Listeners"
  >
  <VIcon
    v-else-if="isVue2"
    v-bind="viconPropsV2"
    v-on="vue2Listeners"
    v-text="resolvedIcon"
  />
  <VIcon
    v-else
    v-bind="viconProps"
  />
</template>

<style scoped lang="scss">
  /* custom-png-icon is a separate class from custom-icon: it is a raster <img>,
     not a CSS mask, so it must not inherit consumers' background-color rules
     (which recolour mask icons for active/disabled/theme state). */
  .custom-icon,
  .custom-png-icon {
    /* default — matches .v-icon--size-default in vuetify3.styl */
    width: 1.5rem;
    height: 1.5rem;

    &.x-small {
      width: 0.75rem;
      height: 0.75rem;
    }

    &.small {
      width: 1rem;
      height: 1rem;
    }

    &.large {
      width: 2.25rem;
      height: 2.25rem;
    }

    &.x-large {
      width: 3rem;
      height: 3rem;
    }
  }

  /* raster icons keep aspect ratio within the size box */
  .custom-png-icon {
    object-fit: contain;
  }
</style>
